import React, { Children, cloneElement, forwardRef } from 'react';

import classNames from 'classnames';
import { randomColor } from 'helpers/helpers';
import PropTypes from 'prop-types';

import useDarkMode from '../hooks/useDarkMode';
import Popovers from './bootstrap/Popovers';

export const AvatarGroup = ({ children, className, size }) => {
	const { darkModeStatus } = useDarkMode();

	return (
		<div className={classNames('avatar-group', className)}>
			<div className='avatar-container'>
				{Children.map(children, (child, index) =>
					index < 3
						? cloneElement(child, {
								borderColor: darkModeStatus ? 'dark' : 'white',
								border: 2,
								color: child.props.color || randomColor(),
								size,
						  })
						: null,
				)}
			</div>
			{children.length > 3 && (
				<Popovers
					desc={Children.map(children, (child, index) =>
						index >= 3 ? (
							<>
								{child.props.userName}
								<br />
							</>
						) : null,
					)}
					trigger='hover'
				>
					<div className='avatar-more' style={{ width: size, height: size }}>
						+{children.length - 3}
					</div>
				</Popovers>
			)}
		</div>
	);
};
AvatarGroup.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
	size: PropTypes.number,
};
AvatarGroup.defaultProps = {
	className: null,
	size: 32,
};

const Avatar = forwardRef(
	(
		{
			border,
			borderColor,
			className,
			color,
			isOnline,
			isReply,
			rounded,
			shadow,
			size,
			src,
			srcSet, // Not used
			userName, // Not used
			...props
		},
		ref,
	) => {
		const _Inner = (
			<img
				alt='Avatar'
				className={classNames(
					'avatar',
					{
						[`rounded${rounded !== 'default' ? `-${rounded}` : ''}`]: rounded,
						'rounded-0': rounded === 0 || rounded === '0',
						[`shadow${shadow !== 'default' ? `-${shadow}` : ''}`]: !!shadow,
						border: !!border,
						[`border-${border}`]: !!border,
						[`border-${borderColor}`]: borderColor,
					},
					`bg-l25-${color}`,
					className,
				)}
				height={size}
				ref={ref}
				src={src}
				srcSet={srcSet}
				width={size}
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...props}
			/>
		);

		if (userName) {
			return (
				<Popovers desc={userName} trigger='hover'>
					{_Inner}
				</Popovers>
			);
		}
		return _Inner;
	},
);
Avatar.propTypes = {
	src: PropTypes.string.isRequired,
	border: PropTypes.oneOf([null, 0, 1, 2, 3, 4, 5]),
	borderColor: PropTypes.oneOf([
		null,
		'primary',
		'secondary',
		'success',
		'info',
		'warning',
		'danger',
		'light',
		'dark',
		'link',
		'brand',
		'brand-two',
		'storybook',
		'white',
	]),
	className: PropTypes.string,
	color: PropTypes.oneOf([
		null,
		'primary',
		'secondary',
		'success',
		'info',
		'warning',
		'danger',
		'light',
		'dark',
		'link',
		'brand',
		'brand-two',
		'storybook',
	]),
	isOnline: PropTypes.bool,
	isReply: PropTypes.bool,
	rounded: PropTypes.oneOf([
		'default',
		0,
		1,
		2,
		3,
		'bottom',
		'top',
		'circle',
		'end',
		'start',
		'pill',
	]),
	shadow: PropTypes.oneOf([null, 'none', 'sm', 'default', 'lg']),
	size: PropTypes.number,
	srcSet: PropTypes.string,
	userName: PropTypes.string,
};
Avatar.defaultProps = {
	border: null,
	borderColor: null,
	className: null,
	color: 'primary',
	isOnline: false,
	isReply: false,
	rounded: 'circle',
	shadow: null,
	size: 128,
	srcSet: null,
	userName: null,
};

export default Avatar;
